import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Subject, takeUntil } from 'rxjs';

import { ToastService } from '@services/common/toast.service';
import { KpiDenominatorActions } from '@state/kpi-denominator.state';
import { OrganizationKpiDenominatorTypeDto } from 'app/@types/dto/organization-kpi-denominator-type-dto.model';
import { revealFormErrors } from 'app/main/helpers/form-validation';

interface AddKpiTypeControls {
    name: FormControl<string | null>;
    unit: FormControl<string | null>;
}

/**
 * Self-contained "add a KPI denominator type" form, shared by the Manage-KPIs dialog and the
 * entity settings form. It owns its form, dispatches the create through KpiDenominatorState,
 * shows its own button spinner, resets on success, and toasts the outcome — so hosts only
 * pass the organization id and the sort order for the new type.
 */
@Component({
    selector: 'app-kpi-type-add-form',
    templateUrl: './kpi-type-add-form.component.html',
    styleUrls: ['./kpi-type-add-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class KpiTypeAddFormComponent implements OnDestroy {
    @Input() organizationId = '';
    /** sortOrder for the new type (typically the current type count). */
    @Input() sortOrder = 0;

    form: FormGroup<AddKpiTypeControls>;
    saving = false;

    private destroy$ = new Subject<void>();

    constructor(
        formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly toastService: ToastService,
        private readonly cdr: ChangeDetectorRef,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.form = formBuilder.group<AddKpiTypeControls>({
            name: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(120)]),
            unit: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(30)]),
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    add(): void {
        if (!revealFormErrors(this.form, this.host.nativeElement) || !this.organizationId) {
            return;
        }
        const completion = new Subject<OrganizationKpiDenominatorTypeDto | undefined>();
        completion.pipe(takeUntil(this.destroy$)).subscribe((created) => {
            this.saving = false;
            if (created) {
                this.form.reset();
                this.toastService.showSuccess({ details: 'results.kpiCarousel.typeAdded' });
            } else {
                this.toastService.showError({ details: 'results.kpiCarousel.saveError' });
            }
            this.cdr.markForCheck();
        });
        this.saving = true;
        this.store.dispatch(
            new KpiDenominatorActions.CreateType(
                this.organizationId,
                {
                    organizationId: this.organizationId,
                    name: this.form.controls.name.value?.trim() ?? '',
                    unit: this.form.controls.unit.value?.trim() ?? '',
                    sortOrder: this.sortOrder,
                },
                completion,
            ),
        );
    }
}
